import React, { useState, useEffect } from 'react'
import { Form, schema } from '@ggs/forms'
import { useI18n } from '@ggs/i18n'
import { useValidators } from '@ggs/hooks'
import { Button } from '../../buttons'
import CircularProgress from '@mui/material/CircularProgress'
import { noop } from 'lodash'
import { useDrupalApi, useDeleteProjectFlow } from '../../../local'
import { addNotification, useDispatch } from '@ggs/store'

import './EditProjectForm.scss'

/**
 * @typedef {import("../../../definitions").ProjectNode} ProjectNode
 */

/**
 * @typedef {Object} EditProjectFormProps
 * @property {function=} closeModal Close modal handler, required when form displaying in a modal.
 * @property {string=} projectId Drupal Project Node ID to edit.
 */

/**
 * Edit existing project form.
 * @param {EditProjectFormProps} props
 * @return {JSX.Element}
 */
export function EditProjectForm({ closeModal = noop, projectId }) {
  const { t } = useI18n()
  const validators = useValidators()
  const successProjectDeleteCallback = () => {
    closeModal()
  }
  const { getProject, updateProject } = useDrupalApi()
  const dispatch = useDispatch()

  /**
   * @type {ProjectNode|null}
   */
  const defaultProjectNode = null
  const [projectNode, setProjectNode] = useState(defaultProjectNode)

  const { ConfirmDialogComponent, confirmProjectDeletion } = useDeleteProjectFlow({
    projectId,
    successProjectDeleteCallback,
  })

  /**
   * Abort add resource operation.
   */
  const abortOperation = () => {
    closeModal()
  }

  /**
   * Handle project update.
   * @param {Object<string, any>} values
   * @param {any} formik
   * @return {Promise<void>}
   */
  const onSubmitHandler = async (values, { setSubmitting }) => {
    const updateProjectNode = {
      ...projectNode,
      title: values?.name || projectNode?.title,
      description: values?.description || projectNode?.description?.value,
    }

    const updatedProjectNode = await updateProject(updateProjectNode)
    if (updatedProjectNode) {
      dispatch(
        addNotification(
          'update_project_success',
          'success',
          t('Project "{{projectName}}" was successfully updated.', {
            projectName: updatedProjectNode.title,
          })
        )
      )

      closeModal()
    } else {
      dispatch(
        addNotification(
          'update_project_error',
          'error',
          t('Update project "{{projectName}}" failed.', {
            projectName: projectNode.title,
          })
        )
      )
    }

    setSubmitting(false)
  }

  const fieldsDefinition = {
    name: {
      label: t('Edit Project Name'),
      type: schema.Forms.FieldTypes.email,
      validator: validators.title,
      fullWidth: true,
    },
    description: {
      label: t('Edit Project Description'),
      type: schema.Forms.FieldTypes.textarea,
      validator: validators.description,
      fullWidth: true,
      inputProps: {
        placeholder: t('Enter a Project Name'),
      },
    },
  }

  const EditProjectForm = Form.useForm({
    withDraftState: false,
    formId: 'form-project__edit',
    className: 'form-project__edit',
    // @ts-ignore
    fields: fieldsDefinition,
    actions: [
      {
        label: t('Delete Project'),
        variant: Button.variant.contained,
        type: 'button',
        className: 'form__action-delete',
        onClick: confirmProjectDeletion,
      },
      {
        label: t('Cancel'),
        variant: Button.variant.contained,
        type: 'button',
        className: 'form__action-cancel',
        onClick: abortOperation,
      },
      {
        type: 'submit',
        label: t('Update Project'),
        className: 'form__action-submit',
        variant: Button.variant.contained,
      },
    ],
    sx: {},
    onSubmit: onSubmitHandler,
  })

  const resolveProjectNode = async () => {
    const projectNode = await getProject(projectId)
    if (projectNode) {
      setProjectNode(projectNode)
    }

    const newFields = {
      ...EditProjectForm.fields,
      name: {
        ...EditProjectForm.fields['name'],
        value: projectNode?.title || '',
      },
      description: {
        ...EditProjectForm.fields['description'],
        value: projectNode?.description?.value || '',
      },
    }

    EditProjectForm.setFields(newFields)
  }

  useEffect(() => {
    resolveProjectNode().then(() => console.log('Project node was resolved.'))
  }, [projectId])

  return (
    <div className="edit-project">
      {!projectNode && <CircularProgress />}
      {projectNode && EditProjectForm.FormComp}
      {ConfirmDialogComponent}
    </div>
  )
}
